import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import DnsIcon from '@mui/icons-material/Dns';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import {
  adminCustomDomainService,
  customPageInviteService,
  eventService,
  parishService,
  personalizedParishService,
  type AdminCustomDomain,
  type CustomPageInvite,
  type PersonalizedParish,
} from '../services';

const inviteStatusColor: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  active: 'success',
  used: 'default',
  revoked: 'error',
  expired: 'warning',
};

const domainStatusColor: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  pending: 'warning',
  active: 'success',
  disabled: 'default',
};

type ParishOption = { id: string; name: string };
type EventOption = { id: string; title: string; slug?: string };

const CustomPageInvitesPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // --- Convites ---
  const [items, setItems] = useState<CustomPageInvite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [suggestedDomain, setSuggestedDomain] = useState('');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0f4a80');
  const [organizationHint, setOrganizationHint] = useState('');
  const [notes, setNotes] = useState('');
  const [enableCustomDomain, setEnableCustomDomain] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState(30);

  // --- Domínios ---
  const [domains, setDomains] = useState<AdminCustomDomain[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [parishes, setParishes] = useState<ParishOption[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [selectedParish, setSelectedParish] = useState<ParishOption | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(null);
  const [domainTargetType, setDomainTargetType] = useState<'landing' | 'event'>('landing');
  const [domainInput, setDomainInput] = useState('');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [dnsHint, setDnsHint] = useState<AdminCustomDomain | null>(null);

  // --- Liberar organizações existentes ---
  const [personalized, setPersonalized] = useState<PersonalizedParish[]>([]);
  const [loadingPersonalized, setLoadingPersonalized] = useState(false);
  const [parishToEnable, setParishToEnable] = useState<ParishOption | null>(null);
  const [allParishesForEnable, setAllParishesForEnable] = useState<ParishOption[]>([]);

  const loadInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const res = await customPageInviteService.list();
      setItems(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar convites.');
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  const loadDomains = useCallback(async () => {
    setLoadingDomains(true);
    try {
      const res = await adminCustomDomainService.list();
      setDomains(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar domínios.');
    } finally {
      setLoadingDomains(false);
    }
  }, []);

  const loadPersonalized = useCallback(async () => {
    setLoadingPersonalized(true);
    try {
      const res = await personalizedParishService.list();
      setPersonalized(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao listar organizações personalizadas.');
    } finally {
      setLoadingPersonalized(false);
    }
  }, []);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  useEffect(() => {
    if (tab === 1) void loadDomains();
    if (tab === 2) {
      void loadPersonalized();
      void parishService
        .getAll({ limit: 300, status: 'active' } as any)
        .then((res) => {
          const list = res.data?.parishes || [];
          setAllParishesForEnable(
            list.map((p: any) => ({
              id: String(p._id || p.id),
              name: p.name,
            }))
          );
        })
        .catch(() => setAllParishesForEnable([]));
    }
  }, [tab, loadDomains, loadPersonalized]);

  useEffect(() => {
    if (!domainDialogOpen) return;
    void parishService
      .getAll({ limit: 200, status: 'active' } as any)
      .then((res) => {
        const list = res.data?.parishes || [];
        setParishes(
          list.map((p: any) => ({
            id: p._id || p.id,
            name: p.name,
          }))
        );
      })
      .catch(() => setParishes([]));
  }, [domainDialogOpen]);

  useEffect(() => {
    if (!selectedParish) {
      setEvents([]);
      setSelectedEvent(null);
      return;
    }
    setLoadingEvents(true);
    void eventService
      .getByParish(selectedParish.id, { limit: 100 } as any)
      .then((res: any) => {
        const list = res.data?.events || res.data || [];
        setEvents(
          (Array.isArray(list) ? list : []).map((e: any) => ({
            id: e._id || e.id,
            title: e.title,
            slug: e.slug,
          }))
        );
      })
      .catch(() => setEvents([]))
      .finally(() => setLoadingEvents(false));
  }, [selectedParish]);

  const resetInviteForm = () => {
    setTitle('');
    setInviteeName('');
    setInviteeEmail('');
    setSuggestedDomain('');
    setHeadline('');
    setDescription('');
    setPrimaryColor('#0f4a80');
    setOrganizationHint('');
    setNotes('');
    setEnableCustomDomain(true);
    setExpiresInDays(30);
  };

  const copyText = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fallback abaixo
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  };

  const copy = async (text: string) => {
    setError(null);
    const ok = await copyText(text);
    if (ok) {
      setSuccess('Copiado!');
    } else {
      setError('Não foi possível copiar automaticamente. Selecione o link e use Cmd/Ctrl+C.');
    }
  };

  const isInviteExpired = (item: CustomPageInvite) => {
    if (item.status === 'expired') return true;
    if (!item.expiresAt) return false;
    return new Date(item.expiresAt).getTime() < Date.now();
  };

  const handleCreateInvite = async () => {
    if (!title.trim()) {
      setError('Informe um título.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await customPageInviteService.create({
        title: title.trim(),
        inviteeName: inviteeName.trim() || undefined,
        inviteeEmail: inviteeEmail.trim() || undefined,
        suggestedDomain: suggestedDomain.trim() || undefined,
        notes: notes.trim() || undefined,
        enableCustomDomain,
        expiresInDays,
        branding: {
          headline: headline.trim() || undefined,
          description: description.trim() || undefined,
          primaryColor,
          organizationHint: organizationHint.trim() || undefined,
        },
      });
      setCreatedUrl(res.data?.url || null);
      setSuccess('Convite criado. Copie o link e envie.');
      setDialogOpen(false);
      resetInviteForm();
      await loadInvites();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao criar convite.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revogar este convite?')) return;
    setBusy(true);
    try {
      await customPageInviteService.revoke(id);
      setSuccess('Convite revogado.');
      await loadInvites();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao revogar.');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerateLink = async (id: string) => {
    if (
      !window.confirm(
        'Gerar um novo link? O link anterior deixa de funcionar.'
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await customPageInviteService.regenerate(id);
      const url = res.data?.url || null;
      setCreatedUrl(url);
      if (url) {
        const copied = await copyText(url);
        setSuccess(
          copied
            ? 'Novo link gerado e copiado.'
            : 'Novo link gerado. Use o botão Copiar no alerta abaixo.'
        );
      } else {
        setSuccess(res.message || 'Novo link gerado.');
      }
      await loadInvites();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao gerar novo link.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenDomainFromInvite = (item: CustomPageInvite) => {
    setTab(1);
    setDomainTargetType('landing');
    setDomainInput(item.suggestedDomain || '');
    setSelectedEvent(null);
    if (item.usedParishId) {
      const found = parishes.find((p) => p.id === item.usedParishId);
      if (found) {
        setSelectedParish(found);
      } else {
        void parishService
          .getAll({ limit: 200, status: 'active' } as any)
          .then((res) => {
            const list = res.data?.parishes || [];
            const opts = list.map((p: any) => ({
              id: String(p._id || p.id),
              name: p.name,
            }));
            setParishes(opts);
            const match = opts.find((p) => p.id === item.usedParishId);
            setSelectedParish(match || null);
          })
          .catch(() => setSelectedParish(null));
      }
    } else {
      setSelectedParish(null);
    }
    setDomainDialogOpen(true);
  };

  const handleSaveDomain = async () => {
    if (!selectedParish || !domainInput.trim()) {
      setError('Selecione a organização e informe o domínio.');
      return;
    }
    if (domainTargetType === 'event' && !selectedEvent) {
      setError('Selecione o evento ou mude para Landing da organização.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminCustomDomainService.upsert(
        domainTargetType === 'landing'
          ? {
              targetType: 'landing',
              parishId: selectedParish.id,
              domain: domainInput.trim(),
            }
          : {
              targetType: 'event',
              eventId: selectedEvent!.id,
              domain: domainInput.trim(),
            }
      );
      setDnsHint(res.data || null);
      setSuccess(
        domainTargetType === 'landing'
          ? 'Domínio da landing salvo. Configure o DNS (A → IP) e verifique.'
          : 'Domínio do evento salvo. Configure o DNS (A → IP) e verifique.'
      );
      setDomainDialogOpen(false);
      setSelectedParish(null);
      setSelectedEvent(null);
      setDomainTargetType('landing');
      setDomainInput('');
      await loadDomains();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar domínio.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyDomain = async (id: string) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminCustomDomainService.verify(id);
      setDnsHint(res.data || null);
      setSuccess('Domínio verificado e ativo.');
      await loadDomains();
    } catch (err: any) {
      if (err?.response?.data?.data) setDnsHint(err.response.data.data);
      setError(err?.response?.data?.message || 'DNS ainda não está correto.');
      await loadDomains();
    } finally {
      setBusy(false);
    }
  };

  const handleEnableParish = async () => {
    if (!parishToEnable) {
      setError('Selecione uma organização.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await personalizedParishService.setEnabled(parishToEnable.id, true);
      setSuccess(`“${parishToEnable.name}” agora pode usar domínio próprio.`);
      setParishToEnable(null);
      await loadPersonalized();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao liberar organização.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisableParish = async (id: string, name: string) => {
    if (!window.confirm(`Remover página personalizada de “${name}”?`)) return;
    setBusy(true);
    try {
      await personalizedParishService.setEnabled(id, false);
      setSuccess('Página personalizada desativada.');
      await loadPersonalized();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao desativar.');
    } finally {
      setBusy(false);
    }
  };

  const publicSiteBase = useMemo(() => {
    const raw = (process.env.REACT_APP_PUBLIC_SITE_URL || 'https://eventos.meusacramento.com').replace(/\/$/, '');
    return raw;
  }, []);

  const landingPreviewUrl = (parishId?: string, slug?: string) => {
    const activeLandingDomain = domains.find(
      (d) =>
        d.parishId === parishId &&
        d.targetType === 'landing' &&
        d.status === 'active' &&
        d.publicUrl
    );
    if (activeLandingDomain?.publicUrl) return activeLandingDomain.publicUrl;
    if (slug) return `${publicSiteBase}/p/${slug}`;
    return publicSiteBase;
  };

  const handlePublishLanding = async (id: string, name: string, slug?: string) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await personalizedParishService.publishLanding(id);
      setSuccess(`Landing de “${name}” publicada.`);
      await loadPersonalized();
      await loadDomains();
      if (slug || id) window.open(landingPreviewUrl(id, slug), '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao publicar landing.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisableDomain = async (id: string) => {
    if (!window.confirm('Desativar este domínio?')) return;
    setBusy(true);
    try {
      await adminCustomDomainService.disable(id);
      setSuccess('Domínio desativado.');
      await loadDomains();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao desativar.');
    } finally {
      setBusy(false);
    }
  };

  const dnsTarget = useMemo(() => dnsHint?.dns?.value || domains[0]?.dns?.value || '31.97.246.238', [
    dnsHint,
    domains,
  ]);
  const dnsType = useMemo(() => dnsHint?.dns?.type || domains[0]?.dns?.type || 'A', [dnsHint, domains]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Páginas personalizadas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Convide organizações e configure o domínio da landing (ou de um evento) sem precisar do login delas.
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Convites de cadastro" />
          <Tab label="Domínios" icon={<DnsIcon />} iconPosition="start" />
          <Tab label="Liberar organização" />
        </Tabs>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {createdUrl && tab === 0 && (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(createdUrl)}>
                Copiar
              </Button>
            }
          >
            Link gerado: {createdUrl}
          </Alert>
        )}
        {dnsHint?.dns && tab === 1 && (
          <Alert severity="info">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              DNS para {dnsHint.domain}
            </Typography>
            {dnsHint.dns.type || 'A'} → <strong>{dnsHint.dns.value}</strong>
            {dnsHint.lastError ? (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {dnsHint.lastError}
              </Typography>
            ) : null}
            <Button size="small" sx={{ ml: 1 }} onClick={() => copy(dnsHint.dns!.value)}>
              Copiar alvo
            </Button>
          </Alert>
        )}

        {tab === 0 && (
          <>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" startIcon={<LinkIcon />} onClick={() => setDialogOpen(true)}>
                Novo convite
              </Button>
            </Stack>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {loadingInvites ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Título</TableCell>
                      <TableCell>Convidado</TableCell>
                      <TableCell>Domínio sugerido</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expira</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            Nenhum convite ainda.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {items.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.inviteeName || item.inviteeEmail || '—'}</TableCell>
                        <TableCell>{item.suggestedDomain || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={item.status} color={inviteStatusColor[item.status] || 'default'} />
                        </TableCell>
                        <TableCell>
                          {item.expiresAt ? new Date(item.expiresAt).toLocaleDateString('pt-BR') : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                            {isInviteExpired(item) || !item.url ? (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LinkIcon />}
                                disabled={busy || item.status === 'used' || item.status === 'revoked'}
                                onClick={() => handleRegenerateLink(item.id)}
                              >
                                Gerar novo link
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ContentCopyIcon />}
                                disabled={busy}
                                onClick={() => {
                                  setCreatedUrl(item.url!);
                                  void copy(item.url!);
                                }}
                              >
                                Copiar link
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DnsIcon />}
                              disabled={busy}
                              onClick={() => handleOpenDomainFromInvite(item)}
                            >
                              Configurar domínio
                            </Button>
                            {item.status === 'active' && !isInviteExpired(item) && (
                              <Button size="small" color="error" disabled={busy} onClick={() => handleRevoke(item.id)}>
                                Revogar
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}

        {tab === 1 && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
            >
              <Typography variant="body2" color="text.secondary">
                Alvo DNS da plataforma: <strong>{dnsType} → {dnsTarget}</strong>
              </Typography>
              <Button
                variant="contained"
                startIcon={<DnsIcon />}
                onClick={() => {
                  setDomainTargetType('landing');
                  setDomainDialogOpen(true);
                }}
              >
                Configurar domínio
              </Button>
            </Stack>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {loadingDomains ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Domínio</TableCell>
                      <TableCell>Organização</TableCell>
                      <TableCell>Destino</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domains.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            Nenhum domínio configurado. Vincule a landing (ou um evento) a um hostname.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {domains.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {d.domain}
                          </Typography>
                          {d.publicUrl && d.status === 'active' && (
                            <Button size="small" href={d.publicUrl} target="_blank" rel="noreferrer">
                              Abrir
                            </Button>
                          )}
                          {d.lastError && (
                            <Typography variant="caption" color="warning.main" display="block">
                              {d.lastError}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{d.parishName || '—'}</TableCell>
                        <TableCell>
                          {d.targetType === 'landing' ? (
                            <Chip size="small" color="primary" label="Landing" />
                          ) : (
                            d.eventTitle || d.eventId || 'Evento'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={d.status} color={domainStatusColor[d.status] || 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {d.status !== 'disabled' && (
                              <Button size="small" disabled={busy} onClick={() => handleVerifyDomain(d.id)}>
                                Verificar DNS
                              </Button>
                            )}
                            {d.status !== 'disabled' && (
                              <Button size="small" color="inherit" disabled={busy} onClick={() => handleDisableDomain(d.id)}>
                                Desativar
                              </Button>
                            )}
                            {d.dns?.value && (
                              <Button size="small" onClick={() => copy(d.dns!.value)}>
                                Copiar DNS
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}

        {tab === 2 && (
          <>
            <Paper sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Transformar organização existente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use quando a paróquia já está cadastrada. Isso libera domínio próprio e página personalizada sem
                precisar de novo convite.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                <Autocomplete
                  sx={{ flex: 1, minWidth: 260 }}
                  options={allParishesForEnable}
                  getOptionLabel={(o) => o.name}
                  value={parishToEnable}
                  onChange={(_, v) => setParishToEnable(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="Buscar organização / paróquia" placeholder="Ex.: Pastoral da Música…" />
                  )}
                />
                <Button variant="contained" disabled={busy || !parishToEnable} onClick={handleEnableParish}>
                  Liberar página personalizada
                </Button>
              </Stack>
            </Paper>

            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {loadingPersonalized ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organização</TableCell>
                      <TableCell>Cidade</TableCell>
                      <TableCell>Landing</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {personalized.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            Nenhuma organização liberada ainda.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {personalized.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.slug}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {[p.city, p.state].filter(Boolean).join(' / ') || '—'}
                        </TableCell>
                        <TableCell>
                          {p.landingPublished ? (
                            <Chip size="small" color="success" label="Publicada" />
                          ) : (
                            <Chip size="small" color="warning" label="Rascunho" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                            {!p.landingPublished && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PublicIcon />}
                                disabled={busy}
                                onClick={() => handlePublishLanding(p.id, p.name, p.slug)}
                              >
                                Publicar landing
                              </Button>
                            )}
                            {p.landingPublished && p.slug && (
                              <Button
                                size="small"
                                href={landingPreviewUrl(p.id, p.slug)}
                                target="_blank"
                                rel="noreferrer"
                                startIcon={<OpenInNewIcon />}
                              >
                                Ver página
                              </Button>
                            )}
                            <Button size="small" color="inherit" disabled={busy} onClick={() => handleDisableParish(p.id, p.name)}>
                              Remover
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DnsIcon />}
                              disabled={busy}
                              onClick={() => {
                                setTab(1);
                                setDomainTargetType('landing');
                                setSelectedParish({ id: p.id, name: p.name });
                                setSelectedEvent(null);
                                setDomainDialogOpen(true);
                              }}
                            >
                              Domínio
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}
      </Stack>

      {/* Dialog convite */}
      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo convite de cadastro</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título interno" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
            <TextField label="Nome do convidado" value={inviteeName} onChange={(e) => setInviteeName(e.target.value)} fullWidth />
            <TextField label="E-mail do convidado" value={inviteeEmail} onChange={(e) => setInviteeEmail(e.target.value)} fullWidth />
            <TextField
              label="Domínio sugerido"
              value={suggestedDomain}
              onChange={(e) => setSuggestedDomain(e.target.value)}
              fullWidth
              placeholder="congresso.suaigreja.org"
            />
            <TextField label="Headline da página" value={headline} onChange={(e) => setHeadline(e.target.value)} fullWidth />
            <TextField
              label="Texto de apoio"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Organização (dica visual)"
              value={organizationHint}
              onChange={(e) => setOrganizationHint(e.target.value)}
              fullWidth
            />
            <TextField label="Cor principal" type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} fullWidth />
            <TextField label="Notas internas" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
            <TextField
              label="Validade (dias)"
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value) || 30)}
              fullWidth
              inputProps={{ min: 1, max: 90 }}
            />
            <FormControlLabel
              control={<Checkbox checked={enableCustomDomain} onChange={(e) => setEnableCustomDomain(e.target.checked)} />}
              label="Liberar domínio personalizado após o cadastro"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreateInvite} disabled={busy}>
            {busy ? 'Criando…' : 'Criar e gerar link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog domínio */}
      <Dialog
        open={domainDialogOpen}
        onClose={() => !busy && setDomainDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configurar domínio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl>
              <FormLabel>O domínio abre</FormLabel>
              <RadioGroup
                row
                value={domainTargetType}
                onChange={(e) => {
                  const next = e.target.value as 'landing' | 'event';
                  setDomainTargetType(next);
                  if (next === 'landing') setSelectedEvent(null);
                }}
              >
                <FormControlLabel value="landing" control={<Radio />} label="Landing da organização" />
                <FormControlLabel value="event" control={<Radio />} label="Um evento específico" />
              </RadioGroup>
            </FormControl>
            <Autocomplete
              options={parishes}
              getOptionLabel={(o) => o.name}
              value={selectedParish}
              onChange={(_, v) => {
                setSelectedParish(v);
                setSelectedEvent(null);
              }}
              renderInput={(params) => <TextField {...params} label="Organização / paróquia" />}
            />
            {domainTargetType === 'event' && (
              <Autocomplete
                options={events}
                getOptionLabel={(o) => o.title}
                value={selectedEvent}
                onChange={(_, v) => setSelectedEvent(v)}
                loading={loadingEvents}
                disabled={!selectedParish}
                renderInput={(params) => <TextField {...params} label="Evento" />}
              />
            )}
            <TextField
              label="Domínio"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              fullWidth
              placeholder="www.zuadaria.com.br"
              helperText={
                domainTargetType === 'landing'
                  ? `Raiz abre a landing. DNS: A → ${dnsTarget}`
                  : `DNS: A → ${dnsTarget}`
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDomainDialogOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveDomain} disabled={busy}>
            Salvar domínio
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomPageInvitesPage;
